import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  Paper,
  Grid
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import EventStats from '../../../models/EventStats';
import Tooltip from '@material-ui/core/Tooltip';
// Import Icons
import InfoIcon from '@material-ui/icons/Info';
import PeopleIcon from '@material-ui/icons/People';
import DevicesIcon from '@material-ui/icons/Devices';
import CommentIcon from '@material-ui/icons/Comment';
import MoodIcon from '@material-ui/icons/Mood';

const useStyles = makeStyles((theme) => ({
  section: {
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[1], // Consistent shadow with other sections
  },
  metricGrid: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  responseContainer: { // Renamed from BinaryVisualization for clarity
    marginBottom: theme.spacing(2)
  },
  sentimentBarContainer: { // Wrapper for sentiment bar + label
    marginBottom: theme.spacing(1),
  },
  sentimentBar: {
    height: 8, // Made slightly thinner
    borderRadius: 4,
    marginTop: theme.spacing(0.5), // Reduced top margin
    transition: 'width 0.5s ease-in-out',
  },
  positiveBar: {
    backgroundColor: theme.palette.success.main // Use theme colors
  },
  neutralBar: {
    backgroundColor: theme.palette.warning.main // Use theme colors
  },
  negativeBar: {
    backgroundColor: theme.palette.error.main // Use theme colors
  },
  sectionTitle: {
     fontWeight: 500, // Match V2 panel style
     display: 'flex',
     alignItems: 'center',
     gap: theme.spacing(1), // Add gap between icon and title
     marginBottom: theme.spacing(2) // Ensure space below title
  },
   metricValue: { // Style for metric values if needed
     fontWeight: 500,
   }
}));

// Reusable Section Header Component (Simplified)
const SectionHeader = ({ icon, title, classes }) => (
   <Box display="flex" alignItems="center" mb={2}>
     {React.cloneElement(icon, { fontSize: 'large' })} {/* Slightly larger icon */}
     <Typography variant="h6" className={classes.sectionTitle} style={{ marginLeft: 8, marginBottom: 0 }}> {/* Removed bottom margin from Typography */}
       {title}
     </Typography>
   </Box>
);

SectionHeader.propTypes = {
  icon: PropTypes.node,
  title: PropTypes.string.isRequired,
  classes: PropTypes.object.isRequired,
};


const CommonStatsDisplay = ({ metrics }) => {
  const classes = useStyles();

  // DEBUG: Log incoming metrics prop
  console.log('[CommonStatsDisplay] metrics:', metrics);

  // Return null or a placeholder if no metrics are available yet
  if (!metrics || Object.keys(metrics).length === 0) {
     // console.log('CommonStatsDisplay: No metrics available.');
    return (
       <Paper className={classes.section}>
           <Typography color="textSecondary">Loading common statistics...</Typography>
       </Paper>
    );
  }

  // console.log('CommonStatsDisplay rendering with metrics:', metrics);


  // Check if metrics has a nested metrics property (from the stats object)
  const metricsData = metrics.metrics || metrics;
  console.log('[CommonStatsDisplay] Accessing metrics data:', metricsData);
  
  // Extract metrics directly to match Results tab display
  const totalViews = metricsData.views || 0;
  const totalActivities = metricsData.activity || 0;
  const uniqueViewers = metricsData.viewed_user || 0;
  const activeParticipants = metricsData.contributed || 0;
  const totalResponses = metricsData.choices || metricsData.contributed || 0;
  const commentsToResponses = metricsData.comment_creator || 0;
  const votes = metricsData.votes || 0;
  // Debug logging
  console.log('[CommonStatsDisplay] Using direct metrics from Results tab - responses:', totalResponses, 'votes:', votes);

  // Access nested userTypes and platformUsage directly
  const userTypes = metrics.userTypes || {};
  const platformUsage = metrics.platformUsage || {};
  const comments = metricsData.comments || 0;
  const commenters = metricsData.comment_creator || 0;
  const likes = metricsData.comment_likes || 0;
  const dislikes = metricsData.comment_dislikes || 0;

  // Get sentiment values directly
  const positiveSent = metricsData.posts_sentiment_positive || 0;
  const neutralSent = metricsData.posts_sentiment_neutral || 0;
  const negativeSent = metricsData.posts_sentiment_negative || 0;
  const totalSentiment = positiveSent + neutralSent + negativeSent;

  // Calculate percentages safely
  const positivePercentage = totalSentiment > 0 ? Math.round((positiveSent / totalSentiment) * 100) : 0;
  const neutralPercentage = totalSentiment > 0 ? Math.round((neutralSent / totalSentiment) * 100) : 0;
  const negativePercentage = totalSentiment > 0 ? Math.round((negativeSent / totalSentiment) * 100) : 0;

  // Determine if sections should be rendered based on data presence
  const hasUserTypes = Object.keys(userTypes).some(key => userTypes[key] > 0);
  const hasPlatformUsage = Object.keys(platformUsage).some(key => platformUsage[key] > 0);
  const hasInteractions = comments > 0 || commenters > 0 || likes > 0 || dislikes > 0;
  const hasSentiment = totalSentiment > 0;


  return (
    <Box>
      {/* Survey Context */}
       <Paper className={classes.section}>
         <SectionHeader icon={<InfoIcon />} title="Survey Context" classes={classes} />
         <Grid container spacing={2}>
           <Grid item xs={6} sm={3}>
             <Tooltip title="Unique users who submitted responses (metrics.idea_creator)"><span>
               <Typography variant="body2">Responses</Typography>
               <Typography className={classes.metricValue}>{totalResponses}</Typography>
             </span></Tooltip>
           </Grid>
           <Grid item xs={6} sm={3}>
             <Tooltip title="Number of times the event was viewed (metrics.views)"><span>
               <Typography variant="body2">Views</Typography>
               <Typography className={classes.metricValue}>{totalViews}</Typography>
             </span></Tooltip>
           </Grid>
           <Grid item xs={6} sm={3}>
             <Tooltip title="Unique users who viewed the event (metrics.viewed_user)"><span>
               <Typography variant="body2">Unique Viewers</Typography>
               <Typography className={classes.metricValue}>{uniqueViewers}</Typography>
             </span></Tooltip>
           </Grid>
           <Grid item xs={6} sm={3}>
             <Tooltip title="Total number of activities (metrics.total_activities)"><span>
               <Typography variant="body2">Contributions</Typography>
               <Typography className={classes.metricValue}>{totalActivities}</Typography>
             </span></Tooltip>
           </Grid>
           <Grid item xs={6} sm={3}>
             <Tooltip title="Unique users who contributed (metrics.contributed)"><span>
               <Typography variant="body2">Active Participants</Typography>
               <Typography className={classes.metricValue}>{activeParticipants}</Typography>
             </span></Tooltip>
           </Grid>
           <Grid item xs={6} sm={3}>
             <Tooltip title="Number of users who commented (metrics.comment_creator)"><span>
               <Typography variant="body2">Comments to Responses</Typography>
               <Typography className={classes.metricValue}>{commentsToResponses}</Typography>
             </span></Tooltip>
           </Grid>
           <Grid item xs={6} sm={3}>
             <Tooltip title="Number of votes on responses (metrics.votes)"><span>
               <Typography variant="body2">Votes</Typography>
               <Typography className={classes.metricValue}>{votes}</Typography>
             </span></Tooltip>
           </Grid>
         </Grid>
       </Paper>

      {/* Combined User Engagement and Platform Distribution */}
      {(hasUserTypes || hasPlatformUsage) && (
        <Paper className={classes.section}>
          <Grid container spacing={3}>
            {/* User Engagement */}
            {hasUserTypes && (
              <Grid item xs={12} md={6}>
                <SectionHeader icon={<PeopleIcon />} title="User Engagement" classes={classes} />
                <Grid container spacing={2} className={classes.metricGrid}>
                  {Object.entries(userTypes)
                    .filter(([_, value]) => value > 0) // Only show types with count > 0
                    .map(([key, value]) => (
                      <Grid item xs={6} key={key}>
                        <Typography variant="body2">{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:</Typography>
                        <Typography className={classes.metricValue}>{value}</Typography>
                      </Grid>
                  ))}
                </Grid>
              </Grid>
            )}
            
            {/* Platform Distribution */}
            {hasPlatformUsage && (
              <Grid item xs={12} md={6}>
                <SectionHeader icon={<DevicesIcon />} title="Platform Distribution" classes={classes} />
                <Grid container spacing={2} className={classes.metricGrid}>
                  {Object.entries(platformUsage)
                    .filter(([_, value]) => value > 0) // Only show platforms with count > 0
                    .map(([key, value]) => (
                      <Grid item xs={6} key={key}>
                        <Typography variant="body2">{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:</Typography>
                        <Typography className={classes.metricValue}>{value}</Typography>
                      </Grid>
                  ))}
                </Grid>
              </Grid>
            )}
          </Grid>
        </Paper>
      )}

      {/* Interaction Metrics */}
      {hasInteractions && (
         <Paper className={classes.section}>
            <SectionHeader icon={<CommentIcon />} title="Interaction Metrics" classes={classes} />
          <Grid container spacing={2} className={classes.metricGrid}>
            <Grid item xs={6} sm={3}><Typography variant="body2">Total Comments:</Typography><Typography className={classes.metricValue}>{comments}</Typography></Grid>
            <Grid item xs={6} sm={3}><Typography variant="body2">Unique Commenters:</Typography><Typography className={classes.metricValue}>{commenters}</Typography></Grid>
            <Grid item xs={6} sm={3}><Typography variant="body2">Comment Likes:</Typography><Typography className={classes.metricValue}>{likes}</Typography></Grid>
            <Grid item xs={6} sm={3}><Typography variant="body2">Comment Dislikes:</Typography><Typography className={classes.metricValue}>{dislikes}</Typography></Grid>
          </Grid>
        </Paper>
      )}

      {/* Sentiment Analysis */}
      {hasSentiment && (
        <Paper className={classes.section}>
           <SectionHeader icon={<MoodIcon />} title="Sentiment Analysis" classes={classes} />
          <Box className={classes.sentimentBarContainer}>
            <Typography variant="body2">Positive: {positivePercentage}%</Typography>
            <Box className={`${classes.sentimentBar} ${classes.positiveBar}`} style={{ width: `${positivePercentage}%` }} />
          </Box>
          <Box className={classes.sentimentBarContainer}>
            <Typography variant="body2">Neutral: {neutralPercentage}%</Typography>
            <Box className={`${classes.sentimentBar} ${classes.neutralBar}`} style={{ width: `${neutralPercentage}%` }} />
          </Box>
          <Box className={classes.sentimentBarContainer}>
            <Typography variant="body2">Negative: {negativePercentage}%</Typography>
            <Box className={`${classes.sentimentBar} ${classes.negativeBar}`} style={{ width: `${negativePercentage}%` }} />
          </Box>
        </Paper>
      )}
    </Box>
  );
};

CommonStatsDisplay.propTypes = {
  metrics: PropTypes.object, // Make it optional initially
};

export default CommonStatsDisplay;